import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

/** Template6 Layout: [LeftFrame][LeftGlass][LeftDoor][Center][RightDoors][RightGlass][RightFrame] */
function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width
  const topY = topFrame.height

  const leftGlass = getParam(params, 'left_glass_panel')
  const leftDoorTop = getParam(params, 'left_door_top_glass_panel')
  const leftDoor = getParam(params, 'left_door_glass_panel')
  const centerGlass = getParam(params, 'center_glass_panel')
  const rightDoorTop = getParam(params, 'right_door_top_glass_panel')
  const rightDoor1 = getParam(params, 'right_door_glass_panel_1')
  const rightDoor2 = getParam(params, 'right_door_glass_panel_2')
  const rightGlass = getParam(params, 'right_glass_panel')
  const rightFrame = getParam(params, 'right_frame_profile')

  if (leftGlass) {
    updateParam(model, 'left_glass_panel', { position: { x: currentX, y: topY, z: leftGlass.position?.z || 0 } })
    currentX += leftGlass.width
  }

  if (leftDoorTop) {
    updateParam(model, 'left_door_top_glass_panel', { position: { x: currentX, y: topY, z: leftDoorTop.position?.z || 0 } })
  }

  if (leftDoor && leftDoorTop) {
    updateParam(model, 'left_door_glass_panel', { position: { x: currentX, y: topY + leftDoorTop.height, z: leftDoor.position?.z || 0 } })
    currentX += leftDoor.width
  }

  if (centerGlass) {
    updateParam(model, 'center_glass_panel', { position: { x: currentX, y: topY, z: centerGlass.position?.z || 0 } })
    currentX += centerGlass.width
  }

  if (rightDoorTop) {
    updateParam(model, 'right_door_top_glass_panel', { position: { x: currentX, y: topY, z: rightDoorTop.position?.z || 0 } })
  }

  if (rightDoor1 && rightDoorTop) {
    updateParam(model, 'right_door_glass_panel_1', { position: { x: currentX, y: topY + rightDoorTop.height, z: rightDoor1.position?.z || 0 } })
    currentX += rightDoor1.width
  }

  if (rightDoor2 && rightDoorTop) {
    updateParam(model, 'right_door_glass_panel_2', { position: { x: currentX, y: topY + rightDoorTop.height, z: rightDoor2.position?.z || 0 } })
    currentX += rightDoor2.width
  }

  if (rightGlass) {
    updateParam(model, 'right_glass_panel', { position: { x: currentX, y: topY, z: rightGlass.position?.z || 0 } })
  }

  if (rightFrame) {
    updateParam(model, 'right_frame_profile', { position: { x: (model.width as number) - rightFrame.width, y: 0, z: 0 } })
  }

  // Update bottom frame positions
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  if (bottomLeft && leftGlass) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { x: leftFrame.width, y: (model.height as number) - bottomLeft.height, z: 0 },
    })
  }

  const bottomCenter = getParam(model.parameters!, 'bottom_center_frame_profile')
  if (bottomCenter && centerGlass) {
    const updatedCenter = getParam(model.parameters!, 'center_glass_panel')
    if (updatedCenter) {
      updateParam(model, 'bottom_center_frame_profile', {
        position: { x: updatedCenter.position!.x, y: (model.height as number) - bottomCenter.height, z: 0 },
      })
    }
  }

  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomRight && rightGlass) {
    const updatedRight = getParam(model.parameters!, 'right_glass_panel')
    if (updatedRight) {
      updateParam(model, 'bottom_right_frame_profile', {
        position: { x: updatedRight.position!.x, y: (model.height as number) - bottomRight.height, z: 0 },
      })
    }
  }
}

function updateGlassPanelHardwarePositionsX(model: ModelData, panelWidth: number, panelId: string) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware) return

  const updatedHardware = { ...panel.hardware }
  const isLeftDoor = panelId === 'left_door_glass_panel'
  const isRightDoor1 = panelId === 'right_door_glass_panel_1'
  const isRightDoor2 = panelId === 'right_door_glass_panel_2'

  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => {
      if (isLeftDoor || isRightDoor1) {
        return { ...lock, position: { ...lock.position, x: panelWidth - glassLockWidth * 1.5 } }
      }
      const ratio = panel.width > 0 ? lock.position.x / panel.width : 0.9
      return { ...lock, position: { ...lock.position, x: panelWidth * ratio } }
    })
  }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => {
      const ratio = panel.width > 0 ? handle.position.x / panel.width : 0.5
      return { ...handle, position: { ...handle.position, x: panelWidth * ratio } }
    })
  }

  if (isRightDoor2 && updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.positionId === 'door_2_right_top_hinge_1') {
        return { ...hinge, position: { ...hinge.position, x: panelWidth - glassHingeWidth / 2 } }
      }
      if (hinge.positionId === 'door_2_right_bottom_hinge_2') {
        return { ...hinge, position: { ...hinge.position, x: panelWidth - glassHingeWidth } }
      }
      return hinge
    })
  }

  updateParam(model, panelId, { hardware: updatedHardware })
}

function updateDoorHardwareForHeight(model: ModelData, doorPanelHeight: number, panelId: string) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware) return

  const updatedHardware = { ...panel.hardware }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => ({
      ...handle, position: { ...handle.position, y: doorPanelHeight * 0.55 },
    }))
  }
  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) return hinge
      return { ...hinge, position: { ...hinge.position, y: doorPanelHeight - glassHingeHeight } }
    })
  }
  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => ({
      ...lock, position: { ...lock.position, y: doorPanelHeight - glassLockHeight },
    }))
  }

  updateParam(model, panelId, { hardware: updatedHardware })
}

const doorPanelIds = ['left_door_glass_panel', 'right_door_glass_panel_1', 'right_door_glass_panel_2'] as const
const bottomFrameIds = ['bottom_left_frame_profile', 'bottom_center_frame_profile', 'bottom_right_frame_profile'] as const

const totalWidth: DimensionHandler = (model, _initial, numValue) => {
  const widthDifference = numValue - (model.width as number)
  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (widthDifference !== 0) {
    const equalShare = widthDifference / 3
    const leftFrame = getParam(model.parameters!, 'left_frame_profile')
    const ft = leftFrame?.width || frameThickness

    const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
    if (bottomLeft) {
      const newW = Math.max(100, bottomLeft.width + equalShare)
      updateParam(model, 'bottom_left_frame_profile', { width: newW })
      updateParam(model, 'left_glass_panel', { width: Math.max(100, newW - ft) })
    }

    const bottomCenter = getParam(model.parameters!, 'bottom_center_frame_profile')
    if (bottomCenter) {
      const newW = Math.max(100, bottomCenter.width + equalShare)
      updateParam(model, 'bottom_center_frame_profile', { width: newW })
      updateParam(model, 'center_glass_panel', { width: newW })
    }

    const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
    if (bottomRight) {
      const newW = Math.max(100, bottomRight.width + equalShare)
      updateParam(model, 'bottom_right_frame_profile', { width: newW })
      updateParam(model, 'right_glass_panel', { width: Math.max(100, newW - ft) })
    }

    recalculatePositions(model)
  }
  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue) => {
  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  const rightDoorTop = getParam(model.parameters!, 'right_door_top_glass_panel')

  if (topFrame && leftDoorTop && rightDoorTop) {
    const doorPanelHeight = numValue - topFrame.height - leftDoorTop.height

    doorPanelIds.forEach((panelId) => {
      const panel = getParam(model.parameters!, panelId)
      if (panel) {
        updateParam(model, panelId, { height: doorPanelHeight })
        updateParam(model, panelId, { position: { ...panel.position, y: topFrame.height + leftDoorTop.height } })
        updateDoorHardwareForHeight(model, doorPanelHeight, panelId)
      }
    })

    const fullHeight = numValue - topFrame.height * 2
    ;['left_glass_panel', 'center_glass_panel', 'right_glass_panel'].forEach((id) => {
      if (getParam(model.parameters!, id)) updateParam(model, id, { height: fullHeight })
    })

    const bottomY = numValue - topFrame.height
    ;[...bottomFrameIds].forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...f.position, y: bottomY } })
    })

    recalculatePositions(model)
  }
  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue) => {
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  const rightDoorTop = getParam(model.parameters!, 'right_door_top_glass_panel')
  const leftDoor = getParam(model.parameters!, 'left_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!leftDoorTop || !rightDoorTop || !leftDoor || !topFrame) return model

  const newTransomPanelHeight = numValue - topFrame.height
  updateParam(model, 'left_door_top_glass_panel', { height: newTransomPanelHeight, position: { ...leftDoorTop.position, y: frameThickness } })
  updateParam(model, 'right_door_top_glass_panel', { height: newTransomPanelHeight, position: { ...rightDoorTop.position, y: frameThickness } })

  doorPanelIds.forEach((panelId) => {
    const panel = getParam(model.parameters!, panelId)
    if (panel) updateParam(model, panelId, { position: { ...panel.position, y: numValue } })
  })

  const newTotalHeight = numValue + leftDoor.height
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  ;['left_glass_panel', 'center_glass_panel', 'right_glass_panel'].forEach((id) => {
    if (getParam(model.parameters!, id)) {
      updateParam(model, id, { height: newTotalHeight - frameThickness - frameThickness })
    }
  })

  ;[...bottomFrameIds].forEach((id) => {
    const f = getParam(model.parameters!, id)
    if (f) updateParam(model, id, { position: { ...f.position, y: newTotalHeight - frameThickness } })
  })

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const leftDoor = getParam(model.parameters!, 'left_door_glass_panel')
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')

  if (!leftDoor || !leftDoorTop) return model

  doorPanelIds.forEach((panelId) => {
    const panel = getParam(model.parameters!, panelId)
    if (panel) {
      updateParam(model, panelId, { height: numValue })
      updateDoorHardwareForHeight(model, numValue, panelId)
    }
  })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  if (topFrame && leftDoorTop) {
    const newTotalHeight = topFrame.height + leftDoorTop.height + numValue
    model.height = newTotalHeight
    updateParam(model, 'left_frame_profile', { height: newTotalHeight })
    updateParam(model, 'right_frame_profile', { height: newTotalHeight })

    ;['left_glass_panel', 'center_glass_panel', 'right_glass_panel'].forEach((id) => {
      if (getParam(model.parameters!, id)) {
        updateParam(model, id, { height: newTotalHeight - frameThickness - frameThickness })
      }
    })

    ;[...bottomFrameIds].forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...f.position, y: newTotalHeight - frameThickness } })
    })
  }

  recalculatePositions(model)
  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  if (!bottomLeft || !leftFrame) return model

  const widthDifference = numValue - bottomLeft.width
  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  updateParam(model, 'left_glass_panel', { width: numValue - leftFrame.width })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const centerFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const centerPanel = getParam(model.parameters!, 'center_glass_panel')
  if (!centerPanel) return model

  const widthDifference = numValue - centerPanel.width
  updateParam(model, 'center_glass_panel', { width: numValue })
  updateParam(model, 'bottom_center_frame_profile', { width: numValue })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (!bottomRight || !rightFrame) return model

  const widthDifference = numValue - bottomRight.width
  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  updateParam(model, 'right_glass_panel', { width: numValue - rightFrame.width })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const rightDoorSectionWidth: DimensionHandler = (model, _initial, numValue) => {
  const rightDoor1 = getParam(model.parameters!, 'right_door_glass_panel_1')
  const rightDoor2 = getParam(model.parameters!, 'right_door_glass_panel_2')
  if (!rightDoor1 || !rightDoor2) return model

  const currentWidth = rightDoor1.width + rightDoor2.width
  const widthDifference = numValue - currentWidth
  const half = widthDifference / 2

  const newR1 = Math.max(200, rightDoor1.width + half)
  const newR2 = Math.max(200, rightDoor2.width + half)

  updateParam(model, 'right_door_glass_panel_1', { width: newR1 })
  updateParam(model, 'right_door_glass_panel_2', { width: newR2 })
  updateParam(model, 'right_door_top_glass_panel', { width: numValue })

  updateGlassPanelHardwarePositionsX(model, newR1, 'right_door_glass_panel_1')
  updateGlassPanelHardwarePositionsX(model, newR2, 'right_door_glass_panel_2')

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const leftDoor = getParam(model.parameters!, 'left_door_glass_panel')
  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: (leftDoor?.width || 0) + numValue,
    individualDoorWidth: [leftDoor?.width || 0, newR1, newR2],
  }

  recalculatePositions(model)
  return model
}

const leftDoorWidth: DimensionHandler = (model, _initial, numValue) => {
  const leftDoor = getParam(model.parameters!, 'left_door_glass_panel')
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  if (!leftDoor || !leftDoorTop) return model

  const widthDifference = numValue - leftDoor.width
  updateParam(model, 'left_door_glass_panel', { width: numValue })
  updateParam(model, 'left_door_top_glass_panel', { width: numValue })
  updateGlassPanelHardwarePositionsX(model, numValue, 'left_door_glass_panel')

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const rightDoor1 = getParam(model.parameters!, 'right_door_glass_panel_1')
  const rightDoor2 = getParam(model.parameters!, 'right_door_glass_panel_2')
  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: numValue + (rightDoor1?.width || 0) + (rightDoor2?.width || 0),
    individualDoorWidth: [numValue, rightDoor1?.width || 0, rightDoor2?.width || 0],
  }

  recalculatePositions(model)
  return model
}

export const model6DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  transomHeight,
  doorPanelHeight,
  leftFrameWidth,
  centerFrameWidth,
  rightFrameWidth,
  rightDoorSectionWidth,
  leftDoorWidth,
}
